import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Pencil } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { GroupPill } from "../../components/GroupPill";
import { ExportMenu } from "../../components/ExportMenu";
import { StudentForm } from "../../components/StudentForm";
import { formatPT, groupLabel } from "../../lib/format";

export default function ReefStudents() {
  const [students, setStudents] = useState([]);
  const [regs, setRegs] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  const load = useCallback(async () => {
    const [s, r, c] = await Promise.all([
      api.get("/students"),
      api.get("/registrations?status=approved"),
      api.get("/classes"),
    ]);
    setStudents(s.data);
    setRegs(r.data);
    setClasses(c.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);
  const classesByStudent = useMemo(() => {
    const m = {};
    regs.forEach((r) => { (m[r.student_id] = m[r.student_id] || []).push(r.class_id); });
    return m;
  }, [regs]);

  const filtered = useMemo(() => {
    let out = students.filter((s) => (filter === "all" ? true : (classesByStudent[s.id] || []).includes(filter)));
    out = [...out];
    if (sort === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "name") out.sort((a, b) => `${a.last_name} ${a.first_name}`.toLowerCase().localeCompare(`${b.last_name} ${b.first_name}`.toLowerCase()));
    else out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [students, filter, sort, classesByStudent]);

  const printCols = [
    { key: "name", label: "Name", render: (s) => `${s.first_name} ${s.last_name}` },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "classes", label: "Classes", render: (s) => (classesByStudent[s.id] || []).map((cid) => groupLabel(classById[cid])).join("; ") || "No class yet" },
    { key: "created_at", label: "Date added", render: (s) => formatPT(s.created_at) },
  ];

  return (
    <div className="space-y-8" data-testid="reef-students">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">The roster</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Students</h1>
          <p className="font-sans text-sm text-stone mt-2">{students.length} on the books.</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportMenu
            list="students"
            params={{ class_id: filter === "all" ? "" : filter, sort }}
            title="Students"
            columns={printCols}
            rows={filtered}
          />
          <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-brass text-sm no-print" data-testid="add-student-btn">
            <Plus size={16} /> Add student
          </button>
        </div>
      </div>

      <div className="space-y-3 no-print">
        <div className="chip-row" data-testid="student-filter-chips">
          <button onClick={() => setFilter("all")} className={`filter-chip px-3 py-1 text-xs font-sans border ${filter === "all" ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid="student-filter-all">
            All ({students.length})
          </button>
          {classes.map((c) => (
            <button key={c.id} onClick={() => setFilter(c.id)} className={`filter-chip px-3 py-1 text-xs font-sans border ${filter === c.id ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid={`student-filter-${c.id}`}>
              {c.admin_label || c.title} ({students.filter((s) => (classesByStudent[s.id] || []).includes(c.id)).length})
            </button>
          ))}
        </div>
        <select className="field-input !py-1.5 text-sm w-full sm:w-48" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="student-sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <div className="space-y-3">
        {filtered.map((s) => (
          <div key={s.id} className="paper-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`student-row-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-serif text-xl text-ink leading-tight">{s.first_name} {s.last_name}</p>
                <StatusMark status={s.status} />
              </div>
              <p className="font-mono text-xs text-stone mt-0.5 break-all">
                {s.email || "—"}{s.phone ? ` · ${s.phone}` : ""} · Date added — {formatPT(s.created_at)}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {(classesByStudent[s.id] || []).length > 0
                  ? (classesByStudent[s.id] || []).map((cid) => <GroupPill key={cid} cls={classById[cid]} />)
                  : <GroupPill cls={null} />}
              </div>
            </div>
            <button onClick={() => { setEditing(s); setOpen(true); }} className="btn-ghost no-print" data-testid={`edit-student-${s.id}`}>
              <Pencil size={15} />
            </button>
          </div>
        ))}
        {filtered.length === 0 && <p className="font-sans text-sm text-stone">Nothing here.</p>}
      </div>

      <StudentForm open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
